import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { useSelector } from 'react-redux'
import cn from 'classnames'
import { FormattedMessage } from 'react-intl'
import { pushToDataLayer } from 'js/pages/LandingPages/utils'
import {
  selectPricingConfig,
  getPlanWithLowestMonthlyPrice,
  selectCoupon,
} from 'js/store/pricingSlice'

import ChooseSubscriptionProduct from './ChooseSubscriptionProduct'
import BestPlanInfo from './BestPlanInfo'
import { mergePricingConfigs } from 'js/utils/mergePricingConfigs'
import LSButton from '../../LSButton'

function ChooseSubscription({
  onSelectSubscription,
  planToSelect,
  include24Months = false,
}) {
  const pricingConfig = useSelector(selectPricingConfig())
  const regularPricingConfig = useSelector(selectPricingConfig(null))
  const coupon = useSelector(selectCoupon)
  const [selectedProduct, setSelectedProduct] = useState({})

  const products = mergePricingConfigs(
    pricingConfig,
    regularPricingConfig,
  ).filter((product) => {
    if (product.duration === 6) return false
    if (product.discount === 100) return true
    if (!include24Months && product.duration === 24) return false
    return true
  })

  useEffect(() => {
    if (!pricingConfig || !regularPricingConfig) return
    if (pricingConfig.products.length < 1) return

    const productsForEffect = mergePricingConfigs(
      pricingConfig,
      regularPricingConfig,
    ).filter((product) => {
      if (product.duration === 6) return false
      if (product.discount === 100) return true
      if (!include24Months && product.duration === 24) return false
      return true
    })
    setSelectedProduct(getPlanWithLowestMonthlyPrice(productsForEffect))
  }, [pricingConfig, regularPricingConfig, include24Months])

  const handleSelectSubscription = (product) => {
    onSelectSubscription(product)
    pushToDataLayer({
      event: 'signup_form',
      section: 'select_subscription',
      label: selectedProduct,
      success: '1', //send 1 if the select subscription is successfull and the user proceeds to the next step successfully, 0 if any error occurs
      error_message: undefined, //if an error occurs when the user selects a subscription plan and proceeds to the next step send error messages, if no error send undefined
    })
  }

  if (!regularPricingConfig) return null

  const currency = pricingConfig.currency
  const bestPlan = getPlanWithLowestMonthlyPrice(products)
  const selected = selectedProduct?.duration ? selectedProduct : bestPlan

  if (planToSelect) {
    return onSelectSubscription(planToSelect)
  }

  return (
    <div className="px-18 lg:px-0">
      <div className="mx-auto max-w-480 pt-18 lg:max-w-none">
        <div
          className={cn(
            `mb-2 flex flex-col-reverse lg:grid lg:grid-cols-${products.length} justify-center gap-0 lg:gap-2`,
            'mx-auto max-w-1024 items-center text-center',
            'rounded-12',
            products.length > 3 ? 'lg:gap-4' : 'lg:gap-2',
          )}
          data-testid="choose-subscription"
        >
          {products.map((product) => {
            return (
              <ChooseSubscriptionProduct
                key={product.duration}
                {...product}
                currency={currency}
                selected={product.duration === selected?.duration}
                onClick={() => {
                  setSelectedProduct(product)
                }}
                isFallbackPrice={coupon && !product.discount}
                bestPlanInfo={
                  bestPlan.duration === product.duration ? (
                    <BestPlanInfo />
                  ) : null
                }
                trialDuration={product.trialDuration}
                onSelectProduct={() => handleSelectSubscription(product)}
              />
            )
          })}
        </div>
        <div className="mb-24">
          {selected?.discount && (
            <div className="pl-4 text-14 lg:text-center">
              <FormattedMessage
                id="chooseSubscription.discountInfo"
                defaultMessage="* off the first subscription period"
              />
            </div>
          )}
        </div>
      </div>
      {selected?.duration && (
        <LSButton
          link={false}
          variant="tertiary"
          size="large"
          upperCase
          onClick={() => handleSelectSubscription(selected)}
          data-testid="choose-button-mobile"
        >
          <FormattedMessage id="lets_begin" defaultMessage="Let's Begin" />
        </LSButton>
      )}
    </div>
  )
}

ChooseSubscription.propTypes = {
  onSelectSubscription: PropTypes.func,
  planToSelect: PropTypes.number,
  include24Months: PropTypes.bool,
}

export default ChooseSubscription
